/**
 * O plano de aplicação da REVISÃO de premissas (passo 4 do assistente):
 * cada proposta da IA vira um item marcável e editável, e aplicar grava
 * pelas rotas de `/prompt-rules` que a aba de Configuração de prompt já
 * usa — editar o texto da premissa, criar outra, ou desativar.
 *
 * Mesma forma do `apply-plan.ts`: função pura dos dois lados, item a
 * item, falha de um não derruba os outros. `before` vem do servidor como
 * o texto GRAVADO, então o lado "antes" da tela é verdade; `after` é o
 * que a pessoa pode editar antes de aplicar.
 */

import type { PostSource, PremiseRevision, PremiseRevisionAction } from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { RULES_TASK } from "./apply-plan";

export type RevisionItem = {
  key: string;
  action: PremiseRevisionAction;
  ruleId: number | null;
  ruleType: string;
  source: PostSource;
  before: string;
  /** Editável; vazio ao desativar. */
  after: string;
  why: string;
  selected: boolean;
  /** Já gravado nesta sessão: sai da seleção e ganha o selo. */
  applied: boolean;
};

export const ACTION_LABEL: Record<PremiseRevisionAction, string> = {
  update: "Editar",
  create: "Criar",
  deactivate: "Desativar"
};

const SOURCE_LABEL: Record<PostSource, string> = {
  network: "Redes",
  press: "Imprensa"
};

export const RULE_TYPE_LABEL: Record<string, string> = {
  global: "Regras globais",
  focus_exclusion_rule: "Exclusão",
  positivo: "Positivo",
  negativo: "Negativo",
  neutro: "Neutro",
  favoravel: "Favorável",
  desfavoravel: "Desfavorável"
};

export function revisionSubtitle(item: Pick<RevisionItem, "source" | "ruleType">): string {
  return `${SOURCE_LABEL[item.source]} · ${RULE_TYPE_LABEL[item.ruleType] ?? item.ruleType}`;
}

export function planRevisions(revisions: PremiseRevision[]): RevisionItem[] {
  return revisions.map((revision, index) => ({
    key: `${revision.action}-${revision.rule_id ?? "new"}-${index}`,
    action: revision.action,
    ruleId: revision.rule_id,
    ruleType: revision.rule_type,
    source: revision.source,
    before: revision.before,
    after: revision.after,
    why: revision.why,
    selected: true,
    applied: false
  }));
}

export type RevisionApi = {
  createPromptRule(
    token: string,
    payload: { brand_id: number; task: string; rule_type: string; source: PostSource; text: string }
  ): Promise<unknown>;
  updatePromptRule(
    token: string,
    ruleId: number,
    payload: { text?: string; is_active?: boolean }
  ): Promise<unknown>;
};

export type RevisionOutcome = {
  applied: string[];
  failed: { key: string; message: string }[];
};

/** Grava o que está marcado e não foi gravado ainda. Texto vazio numa
 *  edição ou criação é recusa local — nada parte pro servidor. */
export async function applyRevisions(
  api: RevisionApi,
  token: string,
  items: RevisionItem[],
  brandId: number
): Promise<RevisionOutcome> {
  const outcome: RevisionOutcome = { applied: [], failed: [] };
  for (const item of items) {
    if (!item.selected || item.applied) continue;
    const text = item.after.trim();
    try {
      if (item.action === "create") {
        if (!text) throw new Error("O texto da premissa está vazio.");
        await api.createPromptRule(token, {
          brand_id: brandId,
          task: RULES_TASK,
          rule_type: item.ruleType,
          source: item.source,
          text
        });
      } else if (item.action === "update") {
        if (item.ruleId === null) throw new Error("Proposta sem premissa de origem.");
        if (!text) throw new Error("O texto da premissa está vazio.");
        await api.updatePromptRule(token, item.ruleId, { text });
      } else {
        if (item.ruleId === null) throw new Error("Proposta sem premissa de origem.");
        await api.updatePromptRule(token, item.ruleId, { is_active: false });
      }
      outcome.applied.push(item.key);
    } catch (caught) {
      outcome.failed.push({
        key: item.key,
        // Era bug (consertado em 2026-10-09, teste de render): lia
        // `caught.message` e perdia o campo e o motivo do 422.
        message: errorMessage(caught, caught instanceof Error ? caught.message : "Não foi possível aplicar.")
      });
    }
  }
  return outcome;
}
